export type ProjectDescription = {
  summary: string;
  sourceTitle: string;
  sourceUrl: string;
  sourceReliability: "official council" | "consultation" | "transport body" | "other";
  note?: string;
};

function normalizeProjectKey(value: string): string {
  return (value ?? "")
    .toLowerCase()
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

const PROJECT_DESCRIPTIONS: Record<string, ProjectDescription> = {
  [normalizeProjectKey("Balerno to Kirknewton NCN")]: {
    summary:
      "A planned National Cycle Network connection between Balerno and Kirknewton, identified in Edinburgh's City Mobility Plan delivery pipeline.",
    sourceTitle: "Delivering the City Mobility Plan",
    sourceUrl: "https://www.edinburgh.gov.uk/city-mobility-plan-1/play-part-developing-future-edinburgh/3",
    sourceReliability: "official council",
    note: "Detailed public design information appears limited.",
  },
  [normalizeProjectKey("Barnton And Whitehouse Road Link To Ncn1")]: {
    summary:
      "A Local Development Plan Action Programme project to connect Barnton and Whitehouse Road with National Cycle Network 1.",
    sourceTitle: "Delivering the City Mobility Plan",
    sourceUrl: "https://www.edinburgh.gov.uk/city-mobility-plan-1/play-part-developing-future-edinburgh/3",
    sourceReliability: "official council",
    note: "Public detail appears to be limited to programme and pipeline references.",
  },
  [normalizeProjectKey("Bughtlin Burn Bridge")]: {
    summary:
      "A planned active travel bridge project listed in Edinburgh's active travel improvements programme.",
    sourceTitle: "Active Travel Improvements Programme January 2026",
    sourceUrl: "https://www.edinburgh.gov.uk/downloads/file/38805/active-travel-improvements-programme-january-2026",
    sourceReliability: "official council",
    note: "The programme source gives status and timing, with limited scope detail.",
  },
  [normalizeProjectKey("Burdiehouse to Mortonhall")]: {
    summary:
      "Walking, wheeling and cycling proposals to improve sustainable travel around Burdiehouse, including Burdiehouse Road and Frogston Road East.",
    sourceTitle: "Walk Wheel Cycle Burdiehouse",
    sourceUrl: "https://consultationhub.edinburgh.gov.uk/consultation_finder/?b_start=210",
    sourceReliability: "consultation",
    note: "The public consultation covers Burdiehouse; the programme names the wider route to Mortonhall.",
  },
  [normalizeProjectKey("Cameron Toll to BioQuarter")]: {
    summary:
      "A walking and cycling route improvement between Cameron Toll and Edinburgh BioQuarter.",
    sourceTitle: "Cameron Toll to BioQuarter walking and cycling route",
    sourceUrl:
      "https://www.edinburgh.gov.uk/cycling-walking-projects-1/cameron-toll-bioquarter-walking-cycling-improvements",
    sourceReliability: "official council",
  },
  [normalizeProjectKey("Charlotte Square")]: {
    summary:
      "A public realm enhancement intended to make Charlotte Square quieter, safer and more welcoming for walking, wheeling and cycling.",
    sourceTitle: "Charlotte Square Public Realm Enhancement",
    sourceUrl: "https://www.edinburgh.gov.uk/downloads/file/37738/charlotte-square-concept-stage-2-engagement-report",
    sourceReliability: "consultation",
  },
  [normalizeProjectKey("City Centre Implementation and Operations Plan")]: {
    summary:
      "A City Centre Transformation workstream covering medium- and long-term operational and movement plans.",
    sourceTitle: "City of Edinburgh Council Questions and Answers, 30 Oct 2025",
    sourceUrl:
      "https://democracy.edinburgh.gov.uk/documents/b27100/Questions%20and%20Answers%2030th-Oct-2025%2010.00%20City%20of%20Edinburgh%20Council.pdf?T=9",
    sourceReliability: "official council",
    note: "This appears to be a programme workstream rather than a single street scheme.",
  },
  [normalizeProjectKey("City Centre West East Link")]: {
    summary:
      "A major cycling and walking route linking the east and west of Edinburgh city centre.",
    sourceTitle: "City Centre West to East Link",
    sourceUrl: "https://www.edinburgh.gov.uk/ccwel/news?page=6",
    sourceReliability: "official council",
  },
  [normalizeProjectKey("Corstorphine Low Traffic Neighbourhood")]: {
    summary:
      "A low traffic neighbourhood designed to create safer, more comfortable local streets for walking, cycling, wheeling and spending time.",
    sourceTitle: "Corstorphine Connections Engagement Summary Report",
    sourceUrl: "https://www.edinburgh.gov.uk/downloads/file/30341/ccltn-placemaking-engagement-report-2021",
    sourceReliability: "consultation",
  },
  [normalizeProjectKey("Cultins Road Shared Footway")]: {
    summary:
      "A shared footway scheme involving widening, resurfacing and crossing upgrades on Cultins Road.",
    sourceTitle: "8% Budget Commitment to Cycling",
    sourceUrl:
      "https://democracy.edinburgh.gov.uk/Data/Transport%20and%20Environment%20Committee/20150317/Agenda/item_73_-_8_budget_commitment_to_cycling.pdf",
    sourceReliability: "official council",
    note: "Older committee papers provide clearer scope than later programme listings.",
  },
  [normalizeProjectKey("Currie and Balerno Active Travel Improvements")]: {
    summary:
      "Proposals focused on safer walking, wheeling and cycling access to Curriehill station, schools, shops and the medical centre.",
    sourceTitle: "Walking, wheeling and cycling improvements in Currie",
    sourceUrl: "https://consultationhub.edinburgh.gov.uk/sfc/currie-walking-wheeling-cycling/",
    sourceReliability: "consultation",
  },
  [normalizeProjectKey("Dalry Town Centre")]: {
    summary:
      "Town centre proposals to make Dalry greener and safer with wider pavements, better cycling provision and improved junctions.",
    sourceTitle: "Consultation opens on future Dalry town centre proposals",
    sourceUrl: "https://www.edinburgh.gov.uk/news/article/13951/consultation-opens-on-future-dalry-town-centre-proposals",
    sourceReliability: "official council",
  },
  [normalizeProjectKey("Deanhaugh Street and Leslie Place")]: {
    summary:
      "A junction improvement scheme to upgrade pedestrian crossings at Deanhaugh Street and Leslie Place.",
    sourceTitle: "Approval of Appointment of Consultants to Design Cycling and Walking Schemes",
    sourceUrl:
      "https://democracy.edinburgh.gov.uk/Data/Finance%20and%20Resources%20Committee/20160609/Agenda/item_716_-_approval_of_appointment_of_consultants_to_design_cycling_and_walking_schemes.pdf",
    sourceReliability: "official council",
  },
  [normalizeProjectKey("Fillyside Crossing")]: {
    summary:
      "A crossing intended to connect Fillyside Road with Seafield promenade for people walking and cycling.",
    sourceTitle: "Active Travel Action Plan 2030",
    sourceUrl: "https://www.spokes.org.uk/wp-content/uploads/2023/07/2306-Active_Travel_Action_Plan_CONSULTATION_final_April_2023.pdf",
    sourceReliability: "other",
    note: "Official sources confirm the project name; this source gives the clearest public description found.",
  },
  [normalizeProjectKey("Fountainbridge - Dundee Street")]: {
    summary:
      "Walking, wheeling and cycling improvements along Dundee Street, Fountainbridge and streets linking to the Union Canal.",
    sourceTitle: "Dundee Street Fountainbridge Active Travel Project",
    sourceUrl: "https://consultationhub.edinburgh.gov.uk/sfc/dundee-street-fountainbridge-active-travel/",
    sourceReliability: "consultation",
  },
  [normalizeProjectKey("George Street and First New Town")]: {
    summary:
      "A redesign of George Street and the First New Town with wider pavements, landscaped seating, active travel priority and a two-way cycleway.",
    sourceTitle: "George Street and First New Town redesign",
    sourceUrl: "https://www.edinburgh.gov.uk/cycling-walking-projects-1/george-street-first-new-town-redesign",
    sourceReliability: "official council",
  },
  [normalizeProjectKey("George Street to Waverley Bridge")]: {
    summary:
      "A future active travel project listed by the council between George Street and Waverley Bridge.",
    sourceTitle: "Active Travel Improvements Programme January 2026",
    sourceUrl: "https://www.edinburgh.gov.uk/downloads/file/38805/active-travel-improvements-programme-january-2026",
    sourceReliability: "official council",
    note: "Dedicated project detail was not found; the official programme confirms name and status.",
  },
  [normalizeProjectKey("Granton Liveable Neighbourhood")]: {
    summary:
      "A future liveable-neighbourhood project in Granton, listed in the City Mobility Plan delivery pipeline and active travel programme.",
    sourceTitle: "Delivering the City Mobility Plan",
    sourceUrl: "https://www.edinburgh.gov.uk/city-mobility-plan-1/play-part-developing-future-edinburgh/3",
    sourceReliability: "official council",
    note: "Dedicated public project detail appears limited.",
  },
  [normalizeProjectKey("Improving Lothian Road")]: {
    summary:
      "Plans to make Lothian Road safer and more accessible with more walking space, segregated cycling, bus improvements and better junctions.",
    sourceTitle: "Improving Lothian Road",
    sourceUrl: "https://www.edinburgh.gov.uk/public-transport/improving-lothian-road/3",
    sourceReliability: "official council",
  },
  [normalizeProjectKey("Kingsknowe Park Crossing")]: {
    summary:
      "A planned crossing project at Kingsknowe Park, listed in the council active travel programme for future construction.",
    sourceTitle: "Active Travel Improvements Programme January 2026",
    sourceUrl: "https://www.edinburgh.gov.uk/downloads/file/38805/active-travel-improvements-programme-january-2026",
    sourceReliability: "official council",
    note: "Dedicated project detail was not found; the official programme confirms name, timing and status.",
  },
  [normalizeProjectKey("LDPAP - Leith Connections")]: {
    summary:
      "Phase 3 of Leith Connections, proposing walking, cycling and public realm improvements between Hawthornvale Path and Seafield.",
    sourceTitle: "Leith Connections Phase 3 Engagement Report",
    sourceUrl: "https://consultationhub.edinburgh.gov.uk/sfc/leithconnectionsphase3/results/leithconnectionsphase3conceptdesignengagementreport.pdf",
    sourceReliability: "consultation",
  },
  [normalizeProjectKey("Leith Connections")]: {
    summary:
      "Street changes in Leith to reduce through traffic, create public spaces and improve walking, wheeling and cycling connections.",
    sourceTitle: "Leith Connections",
    sourceUrl: "https://www.edinburgh.gov.uk/cycling-walking-projects-1/leith-connections",
    sourceReliability: "official council",
  },
  [normalizeProjectKey("Longstone Link")]: {
    summary:
      "A planned active travel bridge route at Longstone, with Phase 1 focused on the Inglis Green Bridge route.",
    sourceTitle: "City Plan 2030 Delivery Programme January 2025",
    sourceUrl: "https://www.edinburgh.gov.uk/downloads/file/36866/city-plan-2030-delivery-programme-2025",
    sourceReliability: "official council",
    note: "The broader project scope may evolve beyond the Phase 1 bridge route.",
  },
  [normalizeProjectKey("Meadows to George Street")]: {
    summary:
      "Transforms walking, cycling, accessibility and public spaces on busy streets linking the Meadows with George Street.",
    sourceTitle: "Meadows to George Street - Streets for People",
    sourceUrl: "https://www.edinburgh.gov.uk/cycling-walking-projects-1/meadows-george-street-streets-people",
    sourceReliability: "official council",
  },
  [normalizeProjectKey("Meadows to Union Canal")]: {
    summary:
      "Improves walking, wheeling and cycling between the Meadows and Union Canal along NCN75, including safer journeys and better public spaces.",
    sourceTitle: "Meadows to Union Canal",
    sourceUrl: "https://www.edinburgh.gov.uk/cycling-walking-projects-1/meadows-union-canal",
    sourceReliability: "official council",
  },
  [normalizeProjectKey("Melville Crescent")]: {
    summary:
      "A project to reimagine Melville Crescent as a more pleasant environment for residents, workers and people passing through.",
    sourceTitle: "Reimagining Melville Crescent",
    sourceUrl: "https://consultationhub.edinburgh.gov.uk/sfc/reimagining-melville-crescent/",
    sourceReliability: "consultation",
  },
  [normalizeProjectKey("NCN 757 South Queensferry to Currie")]: {
    summary:
      "A programme-listed National Cycle Network corridor item; public sources appear inconsistent on the exact NCN number and destination.",
    sourceTitle: "Active Travel Improvements Programme January 2026",
    sourceUrl: "https://www.edinburgh.gov.uk/downloads/file/38805/active-travel-improvements-programme-january-2026",
    sourceReliability: "official council",
    note: "Research found possible confusion with NCN 748 South Queensferry to Ratho.",
  },
  [normalizeProjectKey("NEAT Connections")]: {
    summary:
      "Now called North Edinburgh Connections, proposing safer streets, accessible pavements, protected cycle lanes and better public spaces.",
    sourceTitle: "North Edinburgh Connections",
    sourceUrl: "https://www.edinburgh.gov.uk/cycling-walking/north-edinburgh-connections",
    sourceReliability: "official council",
  },
  [normalizeProjectKey("Niddrie Mains Road")]: {
    summary:
      "Craigmillar Niddrie Connections proposes safer crossings, bus lanes, cycle routes and improved public spaces along Niddrie Mains Road.",
    sourceTitle: "Craigmillar Niddrie Connections",
    sourceUrl: "https://www.edinburgh.gov.uk/cycling-walking/craigmillar-niddrie-connections",
    sourceReliability: "official council",
  },
  [normalizeProjectKey("One-way Street Exemptions")]: {
    summary:
      "Contraflow cycling exemptions on selected one-way streets to improve cycle network connectivity and road safety.",
    sourceTitle: "Statement of Reasons - One-Way Street Exemptions",
    sourceUrl: "https://www.edinburgh.gov.uk/downloads/file/38838/statement-of-reasons",
    sourceReliability: "official council",
  },
  [normalizeProjectKey("Portobello Town Centre (20-minute neighbourhood)")]: {
    summary:
      "A 20-minute neighbourhood project to improve access, public spaces and walking, wheeling and cycling around Portobello town centre.",
    sourceTitle: "Portobello - Living Well Locally",
    sourceUrl: "https://consultationhub.edinburgh.gov.uk/sfc/portobello-town-centre/",
    sourceReliability: "consultation",
  },
  [normalizeProjectKey("Portobello to Musselburgh active travel improvements")]: {
    summary:
      "A planned scheme in Edinburgh's mobility programme to improve active travel connections between Portobello and Musselburgh.",
    sourceTitle: "Delivering the City Mobility Plan",
    sourceUrl: "https://www.edinburgh.gov.uk/city-mobility-plan-1/play-part-developing-future-edinburgh/3",
    sourceReliability: "official council",
    note: "Dedicated design detail was not found.",
  },
  [normalizeProjectKey("Queensferry Connections")]: {
    summary:
      "A package of active travel improvements across Queensferry, including Builyeon Road, Echline Interchange and Ferry Muir Road links.",
    sourceTitle: "Queensferry active travel links",
    sourceUrl: "https://www.edinburgh.gov.uk/queensferryconnections",
    sourceReliability: "official council",
  },
  [normalizeProjectKey("QuietRoute 30 - Dalkeith Road to Ratcliffe Terrace")]: {
    summary:
      "A QuietRoute scheme to improve walking and cycling between Holyrood Park, Dalkeith Road and Ratcliffe Terrace.",
    sourceTitle: "Holyrood Park to Ratcliffe Terrace consultation summary",
    sourceUrl:
      "https://consultationhub.edinburgh.gov.uk/sfc/design-cycle-walk-holyrood-park-ratcliffe-terrace/supporting_documents/7%20Consultation%20summary%20sheet.pdf",
    sourceReliability: "consultation",
  },
  [normalizeProjectKey("QuietRoute 60 - Davidson's Mains Park")]: {
    summary:
      "Improvements to Davidson's Mains Park on QuietRoute 60, focused on widening and lighting the path from Queensferry Road to Barnton Avenue.",
    sourceTitle: "Davidson's Mains Park Walk and Cycle Improvements",
    sourceUrl:
      "https://consultationhub.edinburgh.gov.uk/sfc/design-cycle-walk-davidsons-mains-park/results/20-davidson-s-mains-park-consultation-report.pdf",
    sourceReliability: "consultation",
  },
  [normalizeProjectKey("QuietRoute 9")]: {
    summary:
      "A phased Roseburn to South Gyle QuietRoute project improving junctions and links, with early work around Balgreen and Saughtonhall.",
    sourceTitle: "Roseburn to South Gyle walking, wheeling and cycling improvements",
    sourceUrl:
      "https://consultationhub.edinburgh.gov.uk/consultation_finder/?advanced=1&ar=&au=&b_start=0&de=&in=Cycling+and+walking&pc=&sort_on=iconsultable_enddate&sort_order=descending&st=&tx=",
    sourceReliability: "consultation",
  },
  [normalizeProjectKey("Roseburn Path - Union Canal")]: {
    summary:
      "Constructed Roseburn Path to Union Canal works forming part of the green corridor route, including crossing and access packages.",
    sourceTitle: "Active Travel Improvements Programme January 2026",
    sourceUrl: "https://www.edinburgh.gov.uk/downloads/file/38805/active-travel-improvements-programme-january-2026",
    sourceReliability: "official council",
    note: "This appears to be a programme-name variant for Roseburn to Union Canal construction packages.",
  },
  [normalizeProjectKey("Roseburn to Union Canal")]: {
    summary:
      "A GBP 17 million active travel scheme creating a green corridor and cycleway between Roseburn and the Union Canal.",
    sourceTitle: "Roseburn to Union Canal",
    sourceUrl: "https://www.edinburgh.gov.uk/cycling-walking/roseburn-union-canal",
    sourceReliability: "official council",
  },
  [normalizeProjectKey("Rural Speed Limits")]: {
    summary:
      "A road-safety project reducing speed limits on rural roads to make walking, cycling and horse riding less intimidating.",
    sourceTitle: "Speed Limits Review: 20mph and Rural Roads",
    sourceUrl: "https://consultationhub.edinburgh.gov.uk/sfc/slowerspeeds/",
    sourceReliability: "consultation",
  },
  [normalizeProjectKey("Smokey Brae")]: {
    summary:
      "A project to make Smokey Brae and Marionville safer and more attractive for walking, wheeling and cycling.",
    sourceTitle: "Smokey Brae and Marionville",
    sourceUrl: "https://www.edinburgh.gov.uk/cycling-walking-projects-1/smokey-brae-marionville-improvements",
    sourceReliability: "official council",
  },
  [normalizeProjectKey("St Andrew Square")]: {
    summary:
      "A planned active travel project at St Andrew Square, listed in Edinburgh's programme for future construction.",
    sourceTitle: "Active Travel Improvements Programme January 2026",
    sourceUrl: "https://www.edinburgh.gov.uk/downloads/file/38805/active-travel-improvements-programme-january-2026",
    sourceReliability: "official council",
    note: "Programme sources give timing but limited scope detail.",
  },
  [normalizeProjectKey("Tram Safety Improvements")]: {
    summary:
      "Cycle safety improvements along the existing Edinburgh Tram route to improve conditions for walking and cycling.",
    sourceTitle: "Tram Route Cycle Safety Improvements",
    sourceUrl: "https://consultationhub.edinburgh.gov.uk/sfc/tram-route-cycle-safety-improvements/",
    sourceReliability: "consultation",
  },
  [normalizeProjectKey("Travelling Safely buildouts improvements")]: {
    summary:
      "A programme converting selected temporary Travelling Safely street changes into constructed improvements.",
    sourceTitle: "Active Travel Improvements Programme January 2026",
    sourceUrl: "https://www.edinburgh.gov.uk/downloads/file/38805/active-travel-improvements-programme-january-2026",
    sourceReliability: "official council",
    note: "Programme source gives phases and status, with limited design detail.",
  },
  [normalizeProjectKey("Travelling safely - upgrading to permanent materials")]: {
    summary:
      "A rolling programme to upgrade retained Travelling Safely cycle, pedestrian and bus measures from temporary infrastructure into permanent materials.",
    sourceTitle: "Travelling Safely North West and Arboretum Place",
    sourceUrl:
      "https://democracy.edinburgh.gov.uk/documents/s87674/Travelling%20Safely%20North%20West%20and%20Arboretum%20Place%20ETRO2127B%20ETRO2130A%20and%20ETRO2323A.pdf",
    sourceReliability: "official council",
  },
  [normalizeProjectKey("West Edinburgh Link")]: {
    summary:
      "A west Edinburgh route improving walking, wheeling and cycling links between The Gyle, Bankhead, South Gyle, Sighthill and Wester Hailes.",
    sourceTitle: "West Edinburgh Link",
    sourceUrl: "https://www.edinburgh.gov.uk/public-transport/west-edinburgh-link",
    sourceReliability: "official council",
  },
};

export function getProjectDescription(project: string): ProjectDescription | null {
  return PROJECT_DESCRIPTIONS[normalizeProjectKey(project)] ?? null;
}
